import React, { Component } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from '~/Providers/Moment';
import Agenda from '~/Containers/Pages/Agenda/Agenda';
import CustomDragLayer from '~/Components/Global/Stickers/CustomDragLayer';
import { setWeekType } from '~/Redux/Modules/DateRange';
import { TABLET_WIDTH, DATE_FORMAT } from '~/Redux/Constants';
import PageSplit from '~/Components/Layout/PageSplit/PageSplit';
import ViewHeader from '~/Containers/Pages/Calendar/ViewHeader/ViewHeader';
import Quote from '~/Containers/Global/Quote/Quote';
import Stickers from '~/Containers/Global/Stickers/Stickers';
import { handleSyncScroll } from '~/Components/Global/Scroll/SyncScroll';
import { addResizeListener, removeResizeListener } from '~/Components/Global/Window/Resize';
import { WEEK_TODO } from '~/Config/Checklists';
import { TaskList } from '~/Components/Pages/Calendar/Tasks';
import Week from '~/Components/Pages/Calendar/WeekView/Week';
import StickerDrop from '~/Containers/Global/Stickers/StickerDrop/StickerDrop';
import { WEEK_VIEW_OUTSIDE } from '~/Config/CalendarViews';
import { toStartOfWeekForChecklist } from '~/Context/Checklist';

import './WeekView.scss';
import { DrawingProvider } from '~/Components/Global/Drawing/DrawingProvider';
import mParticle, { ScreenViewEvent } from '~/Providers/mParticle';
import { filterEventsByCalendarDisplay } from '~/Redux/Modules/Events/selectors';

export class WeekView extends Component {
  constructor() {
    super();

    this.dataLeft = {
      0: true,
      1: true,
      2: true,
      3: false,
      4: false,
      5: false,
      6: false,
    };

    this.dataRight = {
      0: false,
      1: false,
      2: false,
      3: true,
      4: true,
      5: true,
      6: true,
    };

    this.dataBoth = {
      0: true,
      1: true,
      2: true,
      3: true,
      4: true,
      5: true,
      6: true,
    };

    this.state = {
      width: false,
      scrollSet: false,
      expanded: false,
      areTasksCollapsed: localStorage.getItem('isTasksSectionCollapsed') === 'true',
      areMealsCollapsed: localStorage.getItem('isMealsSectionCollapsed') === 'true',
    };
  }

  componentWillUnmount = () => {
    removeResizeListener();
  };

  componentDidMount = () => {
    addResizeListener(this);
    const { match, setWeekType } = this.props;
    mParticle.logEvent(new ScreenViewEvent('Week View'));
    if (match.params.year && match.params.month && match.params.day) {
      const start = moment().set({
        year: match.params.year,
        month: match.params.month - 1,
        date: match.params.day,
      });

      if (start.isValid()) {
        return setWeekType(start.startOf('week'));
      }
      return setWeekType(moment().startOf('week'));
    }
    return setWeekType(moment().startOf('week'));
  };

  componentDidUpdate = (nextProps, nextState) => {
    // to handle resize if the with goes down to mobile we need to reset the left ref to the mobile span
    if (
      (nextState.width > TABLET_WIDTH && this.state.width < TABLET_WIDTH) ||
      (nextState.width < TABLET_WIDTH && this.state.width > TABLET_WIDTH)
    ) {
      this.setState({ scrollSet: false });
    }

    // activating scroll sync
    if (this.rightWeekRef && this.leftWeekRef && !this.state.scrollSet) {
      handleSyncScroll([
        this.leftWeekRef.getElementsByClassName('scroll-child')[0],
        this.rightWeekRef.getElementsByClassName('scroll-child')[0],
      ]);
      this.setState({ scrollSet: true });
    }
  };

  handleCollapseAndExpandTasksClick = () => {
    const { areTasksCollapsed } = this.state;
    const nextAreTasksCollapsed = !areTasksCollapsed;
    localStorage.setItem('isTasksSectionCollapsed', nextAreTasksCollapsed);
    this.setState({ areTasksCollapsed: nextAreTasksCollapsed });
  };

  handleCollapseAndExpandMealsClick = () => {
    const { areMealsCollapsed } = this.state;
    const nextAreMealsCollapsed = !areMealsCollapsed;
    localStorage.setItem('isMealsSectionCollapsed', nextAreMealsCollapsed);
    this.setState({ areMealsCollapsed: nextAreMealsCollapsed });
  };

  toggle = expanded => {
    this.setState({ ...this.state, expanded: expanded });
  };

  renderDesktopLayout = () => {
    if (this.state.width < TABLET_WIDTH) return;
    const { start } = this.props;
    return (
      <PageSplit leftClassName="week-left-side" rightClassName="week-right-side">
        <div className="flex-row">
          <div className="week-side-info flex-1 tablet">
            <ViewHeader />
            <Quote showHeader={false} showFooter={false} />
            <TaskList
              date={toStartOfWeekForChecklist(start)}
              type={WEEK_TODO}
              header="GOALS"
              goalTaskWrapping
            />
            <Stickers />
          </div>
          <div className="flex-3 week-content-wrapper" ref={r => (this.leftWeekRef = r)}>
            <Week
              isLeftSide
              handleTasksSectionClick={this.handleCollapseAndExpandTasksClick}
              handleMealsSectionClick={this.handleCollapseAndExpandMealsClick}
              isTasksSectionCollapsed={this.state.areTasksCollapsed}
              isMealsSectionCollapsed={this.state.areMealsCollapsed}
              showScrollOnHover
              showTimeGrid
              days={this.dataLeft}
              transition={this.transition}
              time={start}
              onDayClick={this.handleTimeChange}
              headerRef={header => (this.leftHeader = header)}
              expanded={this.state.expanded}
              toggleExpand={this.toggle}
            />
          </div>
        </div>
        <div className="flex-row">
          <span className="week-content-wrapper" ref={r => (this.rightWeekRef = r)}>
            <Week
              handleTasksSectionClick={this.handleCollapseAndExpandTasksClick}
              handleMealsSectionClick={this.handleCollapseAndExpandMealsClick}
              isTasksSectionCollapsed={this.state.areTasksCollapsed}
              isMealsSectionCollapsed={this.state.areMealsCollapsed}
              showScrollOnHover
              days={this.dataRight}
              transition={this.transition}
              time={start}
              onDayClick={this.handleTimeChange}
              headerRef={header => (this.rightHeader = header)}
              expanded={this.state.expanded}
              toggleExpand={this.toggle}
            />
          </span>
        </div>
      </PageSplit>
    );
  };

  renderMobileLayout = () => {
    if (this.state.width >= TABLET_WIDTH) return;
    const { start } = this.props;
    return (
      <div className="flex-row">
        <span className="week-content-wrapper full-width">
          <Week
            showScrollOnHover={false}
            days={this.dataBoth}
            showTimeGrid
            transition={this.transition}
            time={start}
            onDayClick={this.handleTimeChange}
            expanded={this.state.expanded}
            toggleExpand={this.toggle}
          />
        </span>
      </div>
    );
  };

  render() {
    //get todays moment for the sticker drop-target
    const { start } = this.props;
    return (
      <Agenda>
        <CustomDragLayer />
        <DrawingProvider
          views={[WEEK_VIEW_OUTSIDE]}
          startDate={toStartOfWeekForChecklist(start).format(DATE_FORMAT)}
          endDate={toStartOfWeekForChecklist(start)
            .add(6, 'days')
            .format(DATE_FORMAT)}
        >
          <StickerDrop
            view={WEEK_VIEW_OUTSIDE}
            date={toStartOfWeekForChecklist(start).format(DATE_FORMAT)}
          >
            {this.renderDesktopLayout()}
            {this.renderMobileLayout()}
          </StickerDrop>
        </DrawingProvider>
      </Agenda>
    );
  }
}

WeekView.propTypes = {
  start: propTypes.object,
  match: propTypes.object,
  setWeekType: propTypes.func,
};

const mapStateToProps = ({ DateRange, Events, Checklists, CalendarAccounts }) => {
  const calendarDisplayOptions = {
    includeMonthView: true,
    includeWeekView: false,
    includeDayView: true,
  };
  const calendarMap = CalendarAccounts.calendarMap;
  const filteredEvents = filterEventsByCalendarDisplay(
    Events.formattedData,
    calendarDisplayOptions,
    calendarMap
  );
  return {
    start: DateRange.start,
    events: filteredEvents,
    todo: Checklists.data,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setWeekType: weekStart => dispatch(setWeekType(weekStart.clone(), false)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WeekView);
